import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import boxShadows from "assets/theme/base/boxShadows";
import pxToRem from "assets/theme/functions/pxToRem";
import card from "assets/theme/components/card";
import button from "assets/theme/components/button";
import { input, inputOutlined, inputLabel, textField, select, autocomplete, checkbox, radio, switchButton, formControlLabel, formLabel } from "assets/theme/components/form";

const { grey, primary, text } = colors;
const { borderRadius } = borders;

const sidenav = {
  styleOverrides: {
    root: { width: pxToRem(250), whiteSpace: "nowrap", border: "none" },
    paper: {
      width: pxToRem(250),
      backgroundColor: colors.white.main,
      borderRight: `1px solid ${grey[200]}`,
      boxShadow: "none",
      height: "100vh",
      margin: 0,
      borderRadius: 0,
      overflow: "auto",
    },
  },
};

const list = {
  styleOverrides: {
    root: { padding: 0 },
  },
};

const listItem = {
  styleOverrides: {
    root: {
      borderRadius: borderRadius.md,
      marginBottom: pxToRem(2),
      "&.Mui-selected": {
        backgroundColor: `rgba(13, 148, 136, 0.08)`,
        color: primary.main,
      },
    },
  },
};

const listItemText = {
  styleOverrides: {
    root: { marginTop: 0, marginBottom: 0 },
  },
};

const menu = {
  styleOverrides: {
    paper: {
      borderRadius: borderRadius.lg,
      boxShadow: boxShadows.lg,
      border: `1px solid ${grey[200]}`,
      padding: `${pxToRem(4)} 0`,
    },
  },
};

const menuItem = {
  styleOverrides: {
    root: {
      fontSize: pxToRem(14),
      padding: `${pxToRem(8)} ${pxToRem(16)}`,
      "&:hover": { backgroundColor: grey[100] },
      "&.Mui-selected": { backgroundColor: `rgba(13, 148, 136, 0.08)` },
    },
  },
};

const divider = {
  styleOverrides: {
    root: { backgroundColor: grey[200], height: pxToRem(1), margin: `${pxToRem(8)} 0`, opacity: 1 },
  },
};

const tooltip = {
  styleOverrides: {
    tooltip: {
      backgroundColor: colors.dark.main,
      color: colors.white.main,
      fontSize: pxToRem(12),
      borderRadius: borderRadius.md,
      padding: `${pxToRem(6)} ${pxToRem(12)}`,
    },
  },
};

const appBar = {
  styleOverrides: {
    root: { boxShadow: "none" },
  },
};

const dialog = {
  styleOverrides: {
    paper: {
      borderRadius: borderRadius.xl,
      boxShadow: boxShadows.xxl,
    },
  },
};

const dialogTitle = {
  styleOverrides: {
    root: { fontSize: pxToRem(18), fontWeight: 600 },
  },
};

const dialogContent = {
  styleOverrides: {
    root: { padding: `${pxToRem(16)} ${pxToRem(24)}` },
  },
};

const dialogActions = {
  styleOverrides: {
    root: { padding: `${pxToRem(12)} ${pxToRem(24)} ${pxToRem(16)}` },
  },
};

const avatar = {
  styleOverrides: {
    root: { fontSize: pxToRem(14), fontWeight: 600 },
  },
};

const icon = {
  styleOverrides: {
    root: { verticalAlign: "middle" },
    fontSizeSmall: { fontSize: `${pxToRem(18)} !important` },
    fontSizeLarge: { fontSize: `${pxToRem(30)} !important` },
  },
};

const popover = {
  styleOverrides: {
    paper: {
      borderRadius: borderRadius.lg,
      boxShadow: boxShadows.lg,
      border: `1px solid ${grey[200]}`,
    },
  },
};

// Table components
const tableContainer = {
  styleOverrides: { root: { boxShadow: "none", borderRadius: borderRadius.xl } },
};

const tableHead = {
  styleOverrides: {
    root: {
      display: "block",
      "& .MuiTableRow-root": {
        "& th": { backgroundColor: grey[100], color: text.secondary, fontSize: pxToRem(12), fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },
      },
    },
  },
};

const tableCell = {
  styleOverrides: {
    root: { padding: `${pxToRem(12)} ${pxToRem(16)}`, borderBottom: `1px solid ${grey[200]}` },
  },
};

const tabs = {
  styleOverrides: {
    root: { position: "relative", borderRadius: borderRadius.xl },
    indicator: { height: pxToRem(3), borderRadius: borderRadius.full, backgroundColor: primary.main },
  },
};

const tab = {
  styleOverrides: {
    root: {
      fontSize: pxToRem(14),
      fontWeight: 500,
      textTransform: "none",
      minWidth: pxToRem(100),
      padding: `${pxToRem(10)} ${pxToRem(20)}`,
      color: text.secondary,
      "&.Mui-selected": { color: primary.main },
    },
  },
};

const linearProgress = {
  styleOverrides: {
    root: { height: pxToRem(4), borderRadius: borderRadius.full, backgroundColor: grey[200] },
    bar: { borderRadius: borderRadius.full },
  },
};

const breadcrumbs = {
  styleOverrides: {
    root: {},
    li: { lineHeight: 0 },
    separator: { fontSize: pxToRem(14), color: grey[400] },
  },
};

const slider = {
  styleOverrides: {
    root: {
      "& .MuiSlider-thumb": { boxShadow: boxShadows.sliderBoxShadow.thumb },
    },
  },
};

const iconButton = {
  styleOverrides: {
    root: { borderRadius: borderRadius.md },
  },
};

const buttonBase = {
  styleOverrides: {
    root: {},
  },
};

const svgIcon = {
  styleOverrides: {
    root: { verticalAlign: "middle" },
    fontSizeSmall: { fontSize: `${pxToRem(18)} !important` },
    fontSizeLarge: { fontSize: `${pxToRem(30)} !important` },
  },
};

const link = {
  styleOverrides: {
    root: { color: primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
  },
};

const container = {
  ".MuiContainer-root": {
    paddingRight: `${pxToRem(24)} !important`,
    paddingLeft: `${pxToRem(24)} !important`,
  },
};

const stepper = { styleOverrides: { root: {} } };
const step = { styleOverrides: { root: {} } };
const stepConnector = { styleOverrides: { root: {} } };
const stepLabel = { styleOverrides: { root: {} } };
const stepIcon = { styleOverrides: { root: { color: grey[400], "&.Mui-active": { color: primary.main }, "&.Mui-completed": { color: primary.main } } } };

export {
  sidenav, list, listItem, listItemText,
  card,
  button, iconButton, buttonBase,
  input, inputOutlined, inputLabel, textField, select, autocomplete,
  checkbox, radio, switchButton, formControlLabel, formLabel,
  menu, menuItem, divider, popover,
  tableContainer, tableHead, tableCell,
  tabs, tab,
  linearProgress, breadcrumbs, slider,
  avatar, tooltip, icon, svgIcon, link,
  appBar, dialog, dialogTitle, dialogContent, dialogActions,
  stepper, step, stepConnector, stepLabel, stepIcon,
  container,
};
